import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Trophy, Truck, Banknote, ArrowRightLeft, Sparkles, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent } from './dialog';
import { Button } from './button';

const FooterNav = ({ counts = {}, accentColor = 'slate' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);
  
  const tabs = [
    { 
      id: 'home', 
      label: 'Home', 
      icon: Home, 
      path: '/tradie',
      count: null // No count for home
    },
    { 
      id: 'win', 
      label: 'Win', 
      icon: Trophy, 
      path: '/tradie/leads-referral',
      count: counts.leads || 0,
      color: 'emerald'
    },
    { 
      id: 'deliver', 
      label: 'Deliver', 
      icon: Truck, 
      path: '/tradie/log-update',
      count: counts.updates || 0,
      color: 'blue'
    },
    { 
      id: 'get-paid', 
      label: 'Get Paid', 
      icon: Banknote, 
      path: '/tradie/claim',
      count: counts.claims || 0,
      color: 'orange'
    },
    { 
      id: 'tap-to-tap', 
      label: 'Tap-to-Tap', 
      icon: ArrowRightLeft, 
      path: null, // Placeholder - opens modal
      color: 'violet'
    },
  ];

  const isActive = (path) => {
    if (!path) return false;
    // Home is active only on exact /tradie path
    if (path === '/tradie') return location.pathname === '/tradie';
    // Win tab active for leads-referral and related capture pages
    if (path === '/tradie/leads-referral') {
      return location.pathname.includes('leads-referral') || 
             location.pathname.includes('capture/scope') ||
             location.pathname.includes('/lead/');
    }
    // Deliver tab active for log-update and related capture pages
    if (path === '/tradie/log-update') {
      return location.pathname.includes('log-update') || 
             location.pathname.includes('generate-swms') ||
             location.pathname.includes('/update/');
    }
    // Get Paid tab active for claim pages
    if (path === '/tradie/claim') {
      return location.pathname.includes('claim');
    }
    return location.pathname.startsWith(path);
  };

  // Determine accent color based on current route
  const getAccentColor = () => {
    if (location.pathname.includes('/win') || location.pathname.includes('/leads')) return 'emerald';
    if (location.pathname.includes('/deliver') || location.pathname.includes('/log-update') || location.pathname.includes('/swms')) return 'blue';
    if (location.pathname.includes('/get-paid') || location.pathname.includes('/claim')) return 'orange';
    return accentColor;
  };

  const currentAccent = getAccentColor();
  const accentColorClass = currentAccent === 'emerald' ? 'bg-emerald-500' 
    : currentAccent === 'blue' ? 'bg-blue-500'
    : currentAccent === 'orange' ? 'bg-orange-500'
    : 'bg-slate-600';

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-50 safe-area-bottom">
        {/* Dark background with accent strip */}
        <div className="bg-slate-900">
          <div className="max-w-lg mx-auto flex items-stretch">
            {tabs.map((tab) => {
              const active = isActive(tab.path);
              const Icon = tab.icon;
              
              return (
                <button
                  key={tab.id}
                  data-testid={`footer-${tab.id}`}
                  onClick={() => {
                    if (tab.path) {
                      navigate(tab.path);
                    } else {
                      setShowContactModal(true);
                    }
                  }}
                  className={`flex-1 flex flex-col items-center justify-center py-2.5 px-1 transition-colors relative`}
                >
                  {/* Active indicator bar */}
                  {active && (
                    <div className={`absolute top-0 left-3 right-3 h-0.5 rounded-full ${
                      tab.color === 'emerald' ? 'bg-emerald-400' 
                      : tab.color === 'blue' ? 'bg-blue-400'
                      : tab.color === 'orange' ? 'bg-orange-400'
                      : tab.color === 'violet' ? 'bg-violet-400'
                      : 'bg-white'
                    }`} />
                  )}
                  
                  <div className="relative">
                    <Icon 
                      className={`w-5 h-5 ${
                        tab.color === 'emerald' ? 'text-emerald-400' 
                        : tab.color === 'blue' ? 'text-blue-400'
                        : tab.color === 'orange' ? 'text-orange-400'
                        : tab.color === 'violet' ? 'text-violet-400'
                        : 'text-white'
                      }`} 
                      strokeWidth={active ? 2.5 : 2} 
                    />
                    {/* Count badge */}
                    {tab.count > 0 && (
                      <span className={`absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center ${
                        tab.color === 'emerald' ? 'bg-emerald-500 text-white' 
                        : tab.color === 'blue' ? 'bg-blue-500 text-white'
                        : tab.color === 'orange' ? 'bg-orange-500 text-white'
                        : 'bg-slate-500 text-white'
                      }`}>
                        {tab.count > 99 ? '99+' : tab.count}
                      </span>
                    )}
                  </div>
                  
                  <span className={`text-[10px] mt-1 font-medium ${
                    active 
                      ? tab.color === 'emerald' ? 'text-emerald-400 font-semibold' 
                      : tab.color === 'blue' ? 'text-blue-400 font-semibold'
                      : tab.color === 'orange' ? 'text-orange-400 font-semibold'
                      : tab.color === 'violet' ? 'text-violet-400 font-semibold'
                      : 'text-white font-semibold'
                      : 'text-slate-400'
                  }`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
          
          {/* Accent color strip at bottom */}
          <div className={`h-1 ${accentColorClass}`} />
        </div>
      </nav>

      {/* Tap-to-Tap Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-sm mx-4 rounded-2xl border-0 p-0 overflow-hidden">
          {/* Compact header with gradient */}
          <div className="bg-gradient-to-br from-violet-500 to-violet-600 px-6 py-5">
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                Tap-to-Tap
              </h2>
            </div>
          </div>
          
          {/* Body */}
          <div className="px-6 py-5">
            <p className="text-slate-600 text-center text-sm mb-1 font-medium">
              Share work. Track progress.
            </p>
            <p className="text-slate-500 text-center text-sm mb-5">
              Keep referrals, work instructions, and invoices moving. This will be absolutely priceless for you.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-semibold rounded-xl"
                onClick={() => {
                  setShowContactModal(false);
                  const subject = encodeURIComponent('2Tap Feature Request: Tap-to-Tap');
                  const body = encodeURIComponent(`Hi 2Tap Team,

I'm interested in the **Tap-to-Tap** feature in 2Tap.

Here are my details:
• Name: ${user?.name || 'N/A'}
• Email: ${user?.email || 'N/A'}

Appreciate it — keen to hear what's possible.

Thanks 2Tap!`);
                  window.location.href = `mailto:tradeofficeai@gmail.com?subject=${subject}&body=${body}`;
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact 2Tap Team
              </Button>
              <Button
                variant="ghost"
                className="w-full h-10 text-slate-500 hover:text-slate-700 font-medium"
                onClick={() => setShowContactModal(false)}
              >
                Maybe later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FooterNav;
